import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ProfileCard from "./ProfileCard";
import PasswordCard from "./PasswordCard";
// import PreferencesCard from "./PreferencesCard";
// import AccessCard from "./AccessCard";
import NotificationsCard from "./NotificationsCard";
import SettingsFeedback from "./SettingsFeedback";

const TABS = [
  {
    id: "profile",
    label: "Profile",
    icon: "bi-person",
    kicker: "Identity",
    description: "How your name, contact, and organization appear across the portal.",
  },
  {
    id: "security",
    label: "Security",
    icon: "bi-shield-lock",
    kicker: "Account safety",
    description: "Update your password. It must be at least 6 characters.",
  },
  // {
  //   id: "preferences",
  //   label: "Preferences",
  //   icon: "bi-sliders",
  //   kicker: "Workspace",
  //   description: "Language, default facility, and the colors used across the portal.",
  // },
  {
    id: "notifications",
    label: "Notifications",
    icon: "bi-bell",
    kicker: "Alerts",
    description: "Choose which updates reach you while you work.",
  },
  // {
  //   id: "access",
  //   label: "Access",
  //   icon: "bi-key",
  //   kicker: "Permissions",
  //   description: "Roles, modules, and facilities assigned to this account.",
  // },
];

const SettingsContent = () => {
  const { userDetail } = useSelector((state) => state.login);
  const [activeTab, setActiveTab] = useState("profile");
  const [feedback, setFeedback] = useState(null);

  const [profile, setProfile] = useState({
    userId: userDetail?.username || "",
    name: userDetail ? `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim() : "",
    email: userDetail?.email || "",
    phone: userDetail?.mobile || "",
    countryCode: userDetail?.countryCode || "",
    organization: userDetail?.tenantId?.tenant_name || "",
    organizationID: userDetail?.tenantId?._id || "",
  });

  useEffect(() => {
    if (!userDetail) return;
    setProfile({
      userId: userDetail.username || "",
      name: `${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim(),
      email: userDetail.email || "",
      phone: userDetail.mobile || "",
      countryCode: userDetail?.countryCode || "",
      organization: userDetail?.tenantId?.tenant_name || "",
      organizationID: userDetail?.tenantId?._id || "",
    });
  }, [userDetail]);

  const [notifications, setNotifications] = useState({
    systemAlerts: true,
    marketing: false,
  });

  const accessData = {
    assignedRoles: "Admin, Manager",
    modulesAccess: "Inventory: Read, Write; Sales: Read",
    facilitiesAccess: "Facility 1, Facility 3",
  };

  const current = TABS.find((tab) => tab.id === activeTab) || TABS[0];

  const initials = useMemo(() => {
    const parts = (profile.name || profile.userId || "U").split(" ").filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "U";
  }, [profile.name, profile.userId]);

  const showSuccess = ({ title, message }) => {
    setFeedback({ title, message });
  };

  return (
    <div className="user-settings">
      <aside className="settings-rail">
        <div className="settings-identity">
          <div className="settings-avatar" aria-hidden="true">{initials}</div>
          <div className="settings-identity__copy">
            <h1>{profile.name || "Your account"}</h1>
            <p>{profile.email || "No email on file"}</p>
            {profile.organization && (
              <span className="settings-org">{profile.organization}</span>
            )}
          </div>
        </div>

        <nav className="settings-nav" aria-label="Settings sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`settings-nav__item${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              aria-current={activeTab === tab.id ? "page" : undefined}
            >
              <span className="settings-nav__icon">
                <i className={`bi ${tab.icon}`} />
              </span>
              <span>
                <strong>{tab.label}</strong>
                <small>{tab.kicker}</small>
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <section className="settings-panel" aria-live="polite">
        <header className="settings-panel__head">
          <p className="settings-kicker">{current.kicker}</p>
          <h2>{current.label}</h2>
          <p className="settings-lead">{current.description}</p>
        </header>

        <div className="settings-panel__body">
          {activeTab === "profile" && (
            <ProfileCard profile={profile} setProfile={setProfile} onSaved={showSuccess} />
          )}
          {activeTab === "security" && <PasswordCard onSaved={showSuccess} />}
          {/* {activeTab === "preferences" && <PreferencesCard onSaved={showSuccess} />} */}
          {activeTab === "notifications" && (
            <NotificationsCard
              notifications={notifications}
              setNotifications={setNotifications}
              onSaved={showSuccess}
            />
          )}
          {/* {activeTab === "access" && <AccessCard access={accessData} />} */}
        </div>
      </section>

      <SettingsFeedback
        open={Boolean(feedback)}
        title={feedback?.title}
        message={feedback?.message}
        onClose={() => setFeedback(null)}
      />
    </div>
  );
};

export default SettingsContent;
